/**
 * Spec 054, step 3b: the safe link's page — the client writes down its accesses, and they go to the
 * secrets store at once, never through a chat. Kostya, 31.10: no channel of its own — whoever gives
 * the link sends it as they like.
 *
 * No login and nothing of the tasker around it: the page reads the code after «#», takes it off the
 * address bar, and speaks to /auth/client-access only, without cookies. Nothing typed here is kept
 * in the browser; after «Сохранить» the link is spent.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { SelectField, TextArea, TextField } from "@/components/ui/Field";
import {
  codeFrom,
  emptyEntry,
  entriesToSend,
  halfFilled,
  KINDS,
  MAX_ENTRIES,
  MAX_VALUE,
  refusalText,
  VALUE_KINDS,
  type AccessEntry,
  type ResourceKind,
  type ValueKind,
} from "@/lib/clientAccess";
import { clock } from "@/lib/lead";

const PAGE = "/access";

/** The page is the client's: main.tsx renders it instead of the tasker. */
export const isAccessPage = (pathname: string) => pathname === PAGE || pathname === `${PAGE}/`;

interface PageInfo { order_label: string; what_md: string | null; expires_at: string }
type State =
  | { step: "loading" }
  | { step: "gone"; text: string }
  | { step: "form"; info: PageInfo }
  | { step: "done"; saved: number };
type Answer<T> = { ok: true; data: T } | { ok: false; text: string };

async function ask<T>(path: "open" | "submit", body: unknown): Promise<Answer<T>> {
  try {
    const response = await fetch(`/auth/client-access/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
    const data = await response.json().catch(() => null);
    if (response.ok) return { ok: true, data: data as T };
    return { ok: false, text: refusalText(response.status, data?.detail) };
  } catch {
    return { ok: false, text: refusalText(0, null) };
  }
}

export function ClientAccessScreen() {
  const [code] = useState(() => codeFrom(window.location.hash));
  const [state, setState] = useState<State>(() => (code ? { step: "loading" } : { step: "gone", text: refusalText(410, null) }));
  const [entries, setEntries] = useState<AccessEntry[]>(() => [emptyEntry()]);
  const [error, setError] = useState<string>();
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // The code leaves the address bar: a screenshot, a bookmark or a shared screen holds nothing.
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    if (!code) return;
    void ask<PageInfo>("open", { code }).then((r) => setState(r.ok ? { step: "form", info: r.data } : { step: "gone", text: r.text }));
  }, [code]);

  const set = (index: number, patch: Partial<AccessEntry>) =>
    setEntries((all) => all.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  const ready = entriesToSend(entries);
  const half = entries.some(halfFilled);

  const send = async () => {
    setError(undefined);
    setSending(true);
    const r = await ask<{ saved: number }>("submit", { code, entries: ready });
    setSending(false);
    if (r.ok) {
      setEntries([emptyEntry()]);
      setState({ step: "done", saved: r.data.saved });
    } else setError(r.text);
  };

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-[640px] flex-col gap-4 px-4 py-8">
      <header className="flex flex-col gap-1">
        <p className="text-meta text-p-text-muted">«Просто»</p>
        <h1 className="text-[22px] font-semibold leading-7 text-p-text">
          {state.step === "form" ? `Доступы для заказа ${state.info.order_label}` : "Доступы"}
        </h1>
      </header>
      {state.step === "loading" && <p className="text-body text-p-text-muted">Открываем…</p>}
      {state.step === "gone" && <p role="alert" className="text-body text-p-text">{state.text}</p>}
      {state.step === "done" && (
        <p role="status" className="text-body text-p-text">
          Готово: сохранено {state.saved}. Ссылка больше не работает — страницу можно закрыть.
        </p>
      )}
      {state.step === "form" && (
        <>
          {state.info.what_md && <p className="whitespace-pre-wrap rounded-xl border border-p-border px-3 py-2 text-body text-p-text">{state.info.what_md}</p>}
          <p className="text-meta text-p-text-muted">
            Впишите доступы — они сразу попадут в наше защищённое хранилище и не пройдут через переписку. Ссылка работает один раз, до {clock(state.info.expires_at)}.
          </p>
          {entries.map((entry, index) => (
            <EntryFields key={index} index={index} entry={entry} onChange={(patch) => set(index, patch)}
              onRemove={entries.length > 1 ? () => setEntries((all) => all.filter((_, i) => i !== index)) : undefined} />
          ))}
          {entries.length < MAX_ENTRIES && (
            <Button variant="secondary" onClick={() => setEntries((all) => [...all, emptyEntry()])}>Ещё доступ</Button>
          )}
          {half && <p className="text-meta text-p-text-muted">У каждого доступа нужны и название, и пароль или ключ.</p>}
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button variant="strong" block disabled={!ready.length || half} loading={sending} loadingText="Сохраняем…" onClick={() => void send()}>
            Сохранить
          </Button>
        </>
      )}
    </main>
  );
}

function EntryFields({ index, entry, onChange, onRemove }: {
  index: number;
  entry: AccessEntry;
  onChange: (patch: Partial<AccessEntry>) => void;
  onRemove?: () => void;
}) {
  const key = entry.value_kind === "key";
  return (
    <fieldset className="flex flex-col gap-3 rounded-xl border border-p-border px-3 py-3">
      <legend className="px-1 text-meta font-semibold text-p-text">Доступ {index + 1}</legend>
      <TextField label="Что это" placeholder="Сервер 1С, хостинг сайта" value={entry.title} maxLength={200}
        onChange={(e) => onChange({ title: e.target.value })} />
      <SelectField label="Вид" value={entry.kind} options={KINDS} onChange={(v) => onChange({ kind: (v || "other") as ResourceKind })} />
      <TextField label="Адрес" placeholder="IP, сайт или домен" value={entry.address} maxLength={500} autoComplete="off"
        onChange={(e) => onChange({ address: e.target.value })} />
      <TextField label="Логин" value={entry.login} maxLength={200} autoComplete="off" onChange={(e) => onChange({ login: e.target.value })} />
      <SelectField label="Что вписываете" value={entry.value_kind} options={VALUE_KINDS}
        onChange={(v) => onChange({ value_kind: (v || "password") as ValueKind })} />
      {key ? (
        <TextArea label="Ключ" value={entry.value} rows={6} maxLength={MAX_VALUE} spellCheck={false} autoComplete="off"
          className="font-mono text-[13px]" onChange={(e) => onChange({ value: e.target.value })} />
      ) : (
        <TextField label="Пароль или токен" type="password" value={entry.value} maxLength={MAX_VALUE} autoComplete="new-password"
          onChange={(e) => onChange({ value: e.target.value })} />
      )}
      <TextField label="Комментарий" placeholder="Без паролей: они — в поле выше" value={entry.comment} maxLength={1000}
        onChange={(e) => onChange({ comment: e.target.value })} />
      {onRemove && <Button size="compact" variant="ghost" onClick={onRemove}>Убрать</Button>}
    </fieldset>
  );
}
